import {
  DefectCatalogItem,
  DefectMeasurementSlug,
  DefectSlug,
  DefectSurveyYear,
  DefectVisualizationRule,
} from '../contracts/availableLayers';
import { DefectRulesetSlug } from '../contracts/defectRulesets';
import {
  Recipe,
  RecipeComponent,
  RecipeComponentFailure,
  RecipeExpansionResult,
  RecipeRuntimeLayer,
} from '../contracts/recipes';
import { toDefectOverlayLayerKind } from '../defectOverlays/overlayKinds';

function humanizeSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function findDefect(
  defects: readonly DefectCatalogItem[],
  component: RecipeComponent,
): DefectCatalogItem | null {
  if (component.defectSlug) {
    return defects.find((defect) => defect.slug === component.defectSlug) ?? null;
  }

  const measurementHint = component.measurementNameHint
    ? normalize(component.measurementNameHint)
    : null;
  const surveyTypeHint = component.surveyTypeHint
    ? normalize(component.surveyTypeHint)
    : null;

  for (const defect of defects) {
    if (surveyTypeHint && normalize(defect.surveyType) !== surveyTypeHint) {
      continue;
    }
    if (!measurementHint) {
      continue;
    }
    const hasMeasurement = defect.measurements.some(
      (measurement) =>
        normalize(measurement.name) === measurementHint ||
        normalize(String(measurement.slug)) === measurementHint,
    );
    if (hasMeasurement) {
      return defect;
    }
  }

  return null;
}

function findMeasurement(
  defect: DefectCatalogItem,
  component: RecipeComponent,
): DefectVisualizationRule | null {
  if (component.measurementSlug) {
    return (
      defect.measurements.find(
        (measurement) => measurement.slug === component.measurementSlug,
      ) ?? null
    );
  }

  const hint = component.measurementNameHint
    ? normalize(component.measurementNameHint)
    : null;
  if (!hint) {
    return null;
  }

  return (
    defect.measurements.find(
      (measurement) =>
        normalize(measurement.name) === hint ||
        normalize(String(measurement.slug)) === hint,
    ) ?? null
  );
}

function resolveSurveyYear(
  measurement: DefectVisualizationRule,
  component: RecipeComponent,
): DefectSurveyYear | null {
  if (component.yearRule === 'FIXED') {
    if (!component.fixedSurveyYearSlug) {
      return null;
    }
    return (
      measurement.surveyYears.find(
        (year) => year.slug === component.fixedSurveyYearSlug,
      ) ?? null
    );
  }

  if (measurement.surveyYears.length === 0) {
    return null;
  }

  return [...measurement.surveyYears].sort((left, right) =>
    right.endDate.localeCompare(left.endDate),
  )[0] ?? null;
}

function resolveLanes(
  surveyYear: DefectSurveyYear,
  component: RecipeComponent,
): readonly string[] {
  if (component.laneRule === 'FIXED') {
    return (component.fixedLanes ?? []).filter((lane) =>
      surveyYear.lanes.includes(lane),
    );
  }
  if (component.laneRule === 'ALL') {
    return surveyYear.lanes;
  }
  const first = surveyYear.lanes[0];
  return first ? [first] : [];
}

function buildDisplayName(
  recipeName: string,
  componentLabel: string,
  surveyYearLabel: string,
  lane: string,
): string {
  return `${recipeName} – ${componentLabel} – ${surveyYearLabel} – ${humanizeSlug(lane)}`;
}

function expandComponent(
  recipe: Recipe,
  component: RecipeComponent,
  defects: readonly DefectCatalogItem[],
  activeLayerIds: ReadonlySet<string>,
  buildLayerId: (input: {
    readonly defectSlug: DefectSlug;
    readonly measurementSlug: DefectMeasurementSlug;
    readonly surveyYearSlug: DefectSurveyYear['slug'];
    readonly rulesetSlug: DefectRulesetSlug;
    readonly lane: string;
  }) => string,
): {
  readonly layers: readonly RecipeRuntimeLayer[];
  readonly failures: readonly RecipeComponentFailure[];
} {
  const defect = findDefect(defects, component);
  if (!defect) {
    return {
      layers: [],
      failures: [
        {
          componentId: component.id,
          label: component.label,
          reason: 'defect_not_found',
        },
      ],
    };
  }

  const measurement = findMeasurement(defect, component);
  if (!measurement) {
    return {
      layers: [],
      failures: [
        {
          componentId: component.id,
          label: component.label,
          reason: 'measurement_not_found',
        },
      ],
    };
  }

  const surveyYear = resolveSurveyYear(measurement, component);
  if (!surveyYear) {
    return {
      layers: [],
      failures: [
        {
          componentId: component.id,
          label: component.label,
          reason: 'survey_year_not_found',
        },
      ],
    };
  }

  const lanes = resolveLanes(surveyYear, component);
  if (lanes.length === 0) {
    return {
      layers: [],
      failures: [
        {
          componentId: component.id,
          label: component.label,
          reason: 'lane_not_found',
        },
      ],
    };
  }

  const layers: RecipeRuntimeLayer[] = [];
  const failures: RecipeComponentFailure[] = [];

  for (const lane of lanes) {
    const layerId = buildLayerId({
      defectSlug: defect.slug,
      measurementSlug: measurement.slug,
      surveyYearSlug: surveyYear.slug,
      rulesetSlug: component.rulesetSlug,
      lane,
    });

    if (activeLayerIds.has(layerId)) {
      failures.push({
        componentId: component.id,
        label: component.label,
        reason: 'already_active',
      });
      continue;
    }

    layers.push({
      recipeId: recipe.id,
      componentId: component.id,
      defectSlug: defect.slug,
      measurementSlug: measurement.slug,
      surveyYearSlug: surveyYear.slug,
      rulesetSlug: component.rulesetSlug,
      lane,
      displayName: buildDisplayName(
        recipe.name,
        component.label,
        surveyYear.surveyYear,
        lane,
      ),
      featureFilter: component.featureFilter ?? null,
      layerKind: toDefectOverlayLayerKind(defect.presentation),
      ...(surveyYear.sourceLayerId
        ? { sourceLayerId: surveyYear.sourceLayerId }
        : {}),
      ...(surveyYear.vectorSourceUrl ? { vectorSourceUrl: surveyYear.vectorSourceUrl } : {}),
    });
  }

  return { layers, failures };
}

export function expandRecipe(
  recipe: Recipe,
  defects: readonly DefectCatalogItem[],
  activeLayerIds: ReadonlySet<string>,
  buildLayerId: (input: {
    readonly defectSlug: DefectSlug;
    readonly measurementSlug: DefectMeasurementSlug;
    readonly surveyYearSlug: DefectSurveyYear['slug'];
    readonly rulesetSlug: DefectRulesetSlug;
    readonly lane: string;
  }) => string,
): RecipeExpansionResult {
  const orderedComponents = [...recipe.components].sort(
    (left, right) => left.displayOrder - right.displayOrder,
  );

  const layers: RecipeRuntimeLayer[] = [];
  const failures: RecipeComponentFailure[] = [];

  for (const component of orderedComponents) {
    const expanded = expandComponent(
      recipe,
      component,
      defects,
      activeLayerIds,
      buildLayerId,
    );
    layers.push(...expanded.layers);
    failures.push(...expanded.failures);
  }

  return {
    recipeId: recipe.id,
    layers,
    failures,
  };
}
